import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Eye, EyeSlash, CheckCircle, Warning } from "@phosphor-icons/react";

export default function OnboardingRegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function validate() {
      try {
        const { data } = await api.get(`/onboarding/invite/${token}`);
        setInvite(data);
      } catch (err) {
        const detail = err.response?.data?.detail || "Invalid or expired invitation link.";
        setError(detail);
      } finally {
        setLoading(false);
      }
    }
    validate();
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }

    setSubmitting(true);
    try {
      await api.post("/onboarding/register", { token, password, phone });
      await checkAuth();
      toast.success("Account created! Welcome to Banks Morgan.");
      navigate("/hub/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-[#0F172A] mb-2">Invitation Error</h2>
        <p className="text-[#6B7280] mb-6">{error}</p>
        <Button onClick={() => navigate("/hub/login")} className="bg-[#0F172A] text-white hover:bg-[#1E293B]">
          Go to Login
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 py-12">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-[#0F172A] px-8 py-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#C5A059] text-[#0F172A] font-extrabold text-lg px-3 py-1.5 rounded-lg">BM</div>
            <div className="text-white font-bold text-lg">Banks Morgan</div>
          </div>
          <h1 className="text-white text-2xl font-bold" data-testid="onboarding-welcome-title">
            Welcome, {invite.name}!
          </h1>
          <p className="text-[#94A3B8] mt-1">Set up your account to get started</p>
        </div>

        {/* Invite Info */}
        <div className="px-8 py-4 bg-[#F0F7FF] border-b border-[#E5E7EB]">
          <div className="flex items-center gap-3">
            <Shield size={20} className="text-[#C5A059]" />
            <div>
              <span className="text-sm text-[#374151]">You have been invited as </span>
              <span className="text-sm font-bold text-[#0F172A]" data-testid="invite-role-label">{invite.role_label}</span>
              {invite.team_name && (
                <span className="text-sm text-[#374151]"> in <span className="font-semibold text-[#0F172A]" data-testid="invite-team-name">{invite.team_name}</span></span>
              )}
              {invite.invited_by_name && (
                <span className="text-sm text-[#6B7280]"> by {invite.invited_by_name}</span>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
          <div>
            <Label className="text-[#374151] font-medium">Email</Label>
            <Input value={invite.email} disabled className="mt-1.5 bg-[#F9FAFB]" data-testid="register-email" />
          </div>

          <div>
            <Label className="text-[#374151] font-medium">Phone (optional)</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 07700 900000"
              className="mt-1.5"
              data-testid="register-phone"
            />
          </div>

          <div>
            <Label className="text-[#374151] font-medium">Password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 characters"
                className="pr-10"
                data-testid="register-password"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-[#374151] font-medium">Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              className="mt-1.5"
              data-testid="register-confirm-password"
            />
          </div>

          {/* What happens next */}
          <div className="bg-[#F9FAFB] rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-[#0F172A] uppercase tracking-wide">What happens next</p>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
              <span className="text-sm text-[#374151]">Review and sign your agreements (NDA + Partner Agreement)</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
              <span className="text-sm text-[#374151]">Complete the Banks Morgan Academy training</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle size={16} className="text-[#C5A059] mt-0.5 shrink-0" />
              <span className="text-sm text-[#374151]">Get full access to your Recruitment Hub dashboard</span>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-bold h-11"
            data-testid="register-submit-btn"
          >
            {submitting ? "Creating Account..." : "Create My Account"}
          </Button>
        </form>
      </div>
    </div>
  );
}
